import React, { useRef, useState, useEffect } from 'react';
import { useEventHub } from '@pwc/event-hub-ui';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { Overlay, Popover } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Divider, useTheme } from '@mui/material';
// import { USER_TYPE_CLASSES } from 'common/constants/General';

import { getLoggedInUser } from '../../redux/security/loggedInUser/loggedInUserSelector';
import './UserInitialSnippet.scss';
import { getUserSettingsSelector } from '../../redux/userSettings/userSettingsSlice';

import LogOut from '../LogOut/LogOut';
import { ThemeSwitch } from '../../../features/defaultTheme/ThemeSwitch';
import {
  getActualUserRole,
  getFakeClientUser,
} from '../../redux/security/loggedInUser/loggedInUserSlice';
import LanguageFilter from '../LanguageSelection/LanguageFilter';
import languageList from '../LanguageSelection/languageList';
import { changeUserLanguageThunk } from '../../redux/userSettings/userSettingsThunk';
import featureFlags from '../../helpers/featureFlags';

const UserInitialsSnippet = ({ handleOnToggleClick, isPopupVisible }) => {
  const theme = useTheme();
  const loggedInUser = useSelector(getLoggedInUser);
  const actualUserRole = useSelector(getActualUserRole);
  const isFakeClientUser = useSelector(getFakeClientUser);
  const { isLanguageSelectionEnabled } = featureFlags;
  const [selectedLanguageState, setSelectedLanguageState] = useState(1);
  const [languageText, setLanguageText] = useState(languageList[0]);
  const dispatch = useDispatch();
  const { eventHub } = useEventHub();
  const isLangUpdated = useRef(false);

  const userSettings = useSelector(getUserSettingsSelector);

  const ref = useRef();

  const darkMode = !!userSettings.settings.darkMode;

  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!isLangUpdated.current && userSettings?.settings?.defaultLanguage) {
      i18n.changeLanguage(userSettings?.settings?.defaultLanguage);
      eventHub.setCurrentLang(userSettings?.settings?.defaultLanguage);
      eventHub.publish('change-language', {
        lng: userSettings?.settings?.defaultLanguage,
      });
      isLangUpdated.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings?.settings?.defaultLanguage]);

  const handleClick = (event) => {
    setShow(!show);
    setTarget(event.target);
    if (show === true) {
      setTimeout(() => {
        document.body.click();
      }, 200);
    }
  };

  useEffect(() => {
    const { settings } = userSettings;
    if (Object.keys(settings).length > 1) {
      const { defaultLanguage } = userSettings.settings;
      if (defaultLanguage !== undefined) {
        if (defaultLanguage === languageList[1].value) {
          setSelectedLanguageState(languageList[1].id);
          setLanguageText(languageList[1]);
        } else {
          setSelectedLanguageState(languageList[0].id);
          setLanguageText(languageList[0]);
        }
      }
    }
  }, [userSettings, userSettings.settings]);

  const setLanguageState = (projectState) => {
    setSelectedLanguageState(projectState);
    setLanguageText(languageList[projectState - 1]);
    const { value } = languageList[projectState - 1];
    dispatch(changeUserLanguageThunk(value));
    i18n.changeLanguage(value);
    eventHub.publish('change-language', { lng: value });
    eventHub.setCurrentLang(value);
  };

  return loggedInUser ? (
    <div className="usersnippet-initials" data-testid="user-initials" ref={ref}>
      <Button onClick={handleClick} sx={{ textDecoration: 'none' }}>
        <Avatar
          data-testid="user-initials-button"
          sx={{
            outline: isFakeClientUser
              ? `2px solid ${theme.palette.clientColor.main}`
              : undefined,
            bgcolor: () => {
              if (
                actualUserRole === USER_ROLES.CLIENT ||
                actualUserRole === USER_ROLES.THIRD_PARTY
              ) {
                return theme.palette.clientColor.main;
              }
              if (isFakeClientUser) {
                return 'transparent';
              }
              return theme.palette.pwcUserColor.main;
            },
            color: theme.palette.text.primary,
            fontSize: '0.875rem',
            width: '30px',
            height: '30px',
          }}
        >
          {loggedInUser.initials}
        </Avatar>
      </Button>

      <Overlay
        show={show}
        target={target}
        placement="bottom"
        rootClose
        onHide={() => {
          setShow(!show);
        }}
        container={ref.current}
        darkMode={!!userSettings.settings.darkMode}
      >
        <Popover
          id="popover-user"
          ref={ref}
          data-testid="popup-menu-user"
          className={darkMode ? 'theme-dark' : 'theme-light'}
        >
          <Popover.Content className="popup-user">
            <div className="user-profile-dropdown-menu-section">
              <p data-testid="popup-user-fullname-message">
                {t('welcomeName')} {loggedInUser.name}
              </p>
            </div>
            {isLanguageSelectionEnabled ? (
              <>
                <Divider sx={{ width: '100%' }} />
                <div className="language-select-top-menue">
                  <h6> {languageText.headerText} </h6>
                  <LanguageFilter
                    items={languageList}
                    selectedItemId={selectedLanguageState}
                    selectItem={setLanguageState}
                  />
                </div>
                <Divider
                  className="divider-lang-select"
                  sx={{ width: '100%' }}
                />
              </>
            ) : null}

            <ThemeSwitch
              handleOnToggleClick={handleOnToggleClick}
              isPopupVisible={isPopupVisible}
            />

            <Divider sx={{ width: '100%' }} />

            <div className="user-profile-dropdown-menu-section">
              <LogOut />
            </div>
          </Popover.Content>
        </Popover>
      </Overlay>
    </div>
  ) : null;
};

export default UserInitialsSnippet;
